import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { useLanguageContext } from '../../context/language_context';
import { search_suggest_url } from '../../utils/constants';
import { getSecureUrl, formatPrice } from '../../utils/helpers';
import axios from 'axios';
import SearchBarContainer from './SearchBar.styles';

const SearchBar = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { isArabic } = useLanguageContext();
  const history = useHistory();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
    if (!isOpen) {
      setQuery('');
      setSuggestions([]);
      setActiveIndex(-1);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Debounced fetch suggestions
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(search_suggest_url, {
        params: { q: searchQuery.trim() },
      });
      if (res.data.success) {
        setSuggestions(res.data.data || []);
      }
    } catch (err) {
      console.error('Search suggest error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setActiveIndex(-1);

    // Debounce: 250ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      history.push(`/products?search=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    history.push(`/products/${suggestion.id}`);
    onClose();
  };

  const handleViewAll = () => {
    if (query.trim()) {
      history.push(`/products?search=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < suggestions.length ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      if (activeIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeIndex]);
      } else {
        // "View all" item
        handleViewAll();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <SearchBarContainer ref={containerRef} dir={isArabic ? 'rtl' : 'ltr'}>
      <form className="search-form" onSubmit={handleSubmit}>
        <div className="search-input-wrapper">
          <FaSearch className="search-input-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder={
              isArabic
                ? 'ابحث عن المنتجات...'
                : 'Search products...'
            }
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck="false"
          />
          {query && (
            <button
              type="button"
              className="clear-btn"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                inputRef.current.focus();
              }}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions dropdown */}
      {(suggestions.length > 0 || loading) && query.trim().length >= 2 && (
        <div className="suggestions-dropdown">
          {loading && suggestions.length === 0 && (
            <div className="suggestion-loading">
              <div className="loading-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}

          {suggestions.map((item, index) => (
            <button
              key={item.id}
              className={`suggestion-item ${index === activeIndex ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(item)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <div className="suggestion-image">
                <img
                  src={getSecureUrl(item.image)}
                  alt={item.name}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23f0f0f0" width="40" height="40"/></svg>';
                  }}
                />
              </div>
              <div className="suggestion-info">
                <span
                  className="suggestion-name"
                  dangerouslySetInnerHTML={{
                    __html: item._highlightedName || item.name,
                  }}
                />
                <span className="suggestion-meta">
                  <span className="suggestion-category">{item.category}</span>
                  <span className="suggestion-price">
                    {item.isOffer && item.originalPrice ? (
                      <>
                        <span className="original-price">
                          {formatPrice(item.originalPrice)}
                        </span>
                        <span className="sale-price">
                          {formatPrice(item.price)}
                        </span>
                      </>
                    ) : (
                      formatPrice(item.price)
                    )}
                  </span>
                </span>
              </div>
              <FaSearch className="suggestion-arrow" />
            </button>
          ))}

          {suggestions.length > 0 && (
            <button
              className={`view-all-btn ${activeIndex === suggestions.length ? 'active' : ''}`}
              onClick={handleViewAll}
              onMouseEnter={() => setActiveIndex(suggestions.length)}
            >
              {isArabic
                ? `عرض جميع النتائج لـ "${query}"`
                : `View all results for "${query}"`}
            </button>
          )}
        </div>
      )}

      {/* No results */}
      {!loading && suggestions.length === 0 && query.trim().length >= 2 && (
        <div className="suggestions-dropdown">
          <div className="no-results">
            {isArabic
              ? `لا توجد نتائج لـ "${query}"`
              : `No results for "${query}"`}
          </div>
        </div>
      )}
    </SearchBarContainer>
  );
};

export default SearchBar;
